import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Printer, 
  Package, 
  Search, 
  Globe, 
  Palette, 
  ShoppingBag,
  CheckCircle,
  Zap,
  Shield,
  Heart
} from 'lucide-react';

const HomePage = () => {
  const services = [
    { id: 'print-on-demand', title: 'Print on Demand', description: 'Custom printing with no minimums', icon: Printer },
    { id: 'dropshipping', title: 'Dropshipping', description: 'Seamless order fulfillment', icon: Package },
    { id: 'product-sourcing', title: 'Product Sourcing', description: 'Quality products at best prices', icon: Search },
    { id: 'global-warehousing', title: 'Global Warehousing', description: 'Strategic storage worldwide', icon: Globe },
    { id: 'custom-branding', title: 'Custom Branding', description: 'Memorable unboxing experience', icon: Palette },
    { id: 'tiktok-shop', title: 'TikTok Shop', description: 'Social commerce fulfillment', icon: ShoppingBag },
  ];

  const features = [
    { icon: Zap, title: 'Fast Processing', description: 'Orders processed within 24-48 hours' },
    { icon: Shield, title: 'Quality Assured', description: '99.5% order accuracy guaranteed' },
    { icon: Heart, title: 'Dedicated Support', description: 'Personal account manager for you' },
  ];

  return (
    <div data-testid="home-page" className="bg-[#fffdf8] relative grain-overlay">
      {/* Hero Section - Horizontal Split with Organic Shape */}
      <section className="min-h-screen pt-20 warm-gradient relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute top-40 left-0 w-64 h-64 bg-[#ea580c]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#fef3c7] rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative z-10"
            >
              <span className="inline-block text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-6">
                Print on Demand & Fulfillment
              </span>
              <h1
                className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#78350f] mb-6 leading-tight"
                data-testid="hero-title"
              >
                Grow Your Business
                <br />
                <span className="text-[#ea580c]">Naturally</span>
              </h1>
              <p
                className="text-[#78350f]/80 text-lg lg:text-xl max-w-lg mb-10 leading-relaxed"
                data-testid="hero-description"
              >
                Comprehensive fulfillment solutions designed to help you flourish. 
                From printing to global shipping, we nurture your success.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="bg-[#ea580c] text-white px-8 py-4 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl inline-flex items-center gap-2 group organic-shape-3"
                >
                  Start Your Journey
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="bg-transparent text-[#78350f] px-8 py-4 font-medium border-2 border-[#78350f] hover:bg-[#78350f] hover:text-white transition-all duration-300 organic-shape-3"
                >
                  Our Services
                </Link>
              </div>
            </motion.div>

            {/* Right - Organic Image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <div className="organic-shape-1 overflow-hidden warm-shadow-lg">
                  <img
                    src="https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt="Print on demand products"
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-6 -left-6 bg-white organic-shape-3 shadow-xl p-5 hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#fef3c7] rounded-full flex items-center justify-center">
                      <CheckCircle size={24} className="text-[#ea580c]" />
                    </div>
                    <div>
                      <span className="font-semibold text-[#78350f] block">5,000+ Sellers</span>
                      <span className="text-sm text-[#78350f]/60">Trust Us Daily</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#78350f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10M+', label: 'Orders Fulfilled' },
              { value: '50+', label: 'Countries Served' },
              { value: '99.5%', label: 'Accuracy Rate' },
              { value: '24/7', label: 'Support' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-[#fcd9b8] text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - Horizontal Scroll Cards */}
      <section className="py-24 lg:py-32 bg-[#fffdf8]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
              What We Offer
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-semibold text-[#78350f]">
              Our Services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={`/services#${service.id}`}
                  data-testid={`service-card-${service.id}`}
                  className="block bg-[#fef3c7]/50 p-8 hover:bg-[#fef3c7] transition-all duration-300 hover:translate-y-[-4px] hover:shadow-xl group organic-shape-2"
                >
                  <div className="w-14 h-14 bg-[#ea580c] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <service.icon size={26} className="text-white" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#78350f] mb-2 group-hover:text-[#ea580c] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[#78350f]/70 mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-[#ea580c] font-medium group-hover:gap-2 transition-all">
                    Learn More
                    <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Zigzag Layout */}
      <section className="py-24 lg:py-32 bg-[#fef3c7]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="organic-shape-2 overflow-hidden warm-shadow-lg">
                <img
                  src="https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Warehouse operations"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
                Why Choose Us
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#78350f] mb-6">
                Built for Your
                <br />
                <span className="text-[#ea580c]">Success</span>
              </h2>
              <p className="text-[#78350f]/80 text-lg mb-10 leading-relaxed">
                We understand the challenges of running an online business. 
                Our solutions are designed to help you grow organically while 
                maintaining quality and customer satisfaction.
              </p>
              
              <div className="space-y-6 mb-10">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-4"
                    data-testid={`feature-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#fef3c7] rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon size={24} className="text-[#ea580c]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#78350f] mb-1">{feature.title}</h4>
                      <p className="text-[#78350f]/70 text-sm">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/about"
                data-testid="about-link"
                className="inline-flex items-center gap-2 text-[#ea580c] font-medium hover:gap-3 transition-all"
              >
                Learn More About Us
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#fffdf8] relative" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-[#78350f] organic-shape-1 p-12 md:p-20 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#ea580c]/20 rounded-full blur-3xl" />
            <div className="relative z-10">
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
                Ready to Transform Your{' '}
                <span className="text-[#fef3c7]">Fulfillment?</span>
              </h2>
              <p className="text-[#fcd9b8] text-lg mb-10 max-w-2xl mx-auto">
                Partner with Granite Current and experience the difference. 
                Start scaling your business today with our warm support.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  data-testid="cta-contact-button"
                  className="bg-[#ea580c] text-white px-8 py-4 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl inline-flex items-center gap-2 group organic-shape-3"
                >
                  Contact Us Today
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/faq"
                  data-testid="cta-faq-button"
                  className="bg-transparent text-white px-8 py-4 font-medium border-2 border-white/30 hover:bg-white/10 transition-all duration-300 organic-shape-3"
                >
                  View FAQ
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
